import { Line } from '@react-three/drei'
import { paraArray, type Vetor3 } from '../../dominio/geometria/vetor3'
import type { ProporcoesVitima } from '../../dominio/didatico/perfisVitima'
import { CORES_SEMANTICAS } from '../../dominio/didatico/tokens'
import type { AtencaoCorporal, RegiaoCorporalAtencao } from '../../dominio/didatico/atencaoCorporal'
import type { MembroAprisionavel } from '../../dominio/didatico/aprisionamentoMembro'
import type { AprisionamentoMembro } from '../../fisica/solucionador'
import type { QuadroCorpo } from '../../fisica/trajetoria'
import type { LeituraBiomecanicaInstante } from '../../dominio/didatico/tonusOcupante'
import { MarcadorCrashTest } from './MarcadorCrashTest'
import { geometriaDeMembro, type FormaDeMembro } from './corpoHumano'
import { Cerebro3D, OrgaosTronco3D } from '../cinematica/OrgaosInternos3D'

interface Props {
  proporcoes: ProporcoesVitima
  pelve: QuadroCorpo
  tronco: QuadroCorpo
  cabeca: QuadroCorpo
  membros: Partial<Record<MembroAprisionavel, QuadroCorpo>>
  atencoes?: readonly AtencaoCorporal[]
  aprisionamentos?: readonly AprisionamentoMembro[]
  mostrarEixoColuna?: boolean
  leituraBiomecanica?: LeituraBiomecanicaInstante
  /** Modo Raio-X com visualização de órgãos internos (3ª colisão do trauma) */
  modoRaioX?: boolean
}

/** Casca translúcida do Raio-X: sem gravar profundidade, os órgãos aparecem por dentro. */
const MATERIAL_RAIO_X = { transparent: true, opacity: 0.22, depthWrite: false } as const

/**
 * Manequim de teste de colisão (Crash-Test Dummy / ATD Hybrid III):
 * - Visual característico em âmbar técnico e grafite de ensaio
 * - Marcadores fotogramétricos (targets) nas têmporas, testa e esterno
 * - Pescoço cervical biomecânico segmentado em anéis elastoméricos
 * - Pivôs articulares visíveis com sinalização luminosa de trauma
 */
export function VitimaRagdoll({
  proporcoes: p,
  pelve,
  tronco,
  cabeca,
  membros,
  atencoes = [],
  aprisionamentos = [],
  mostrarEixoColuna = false,
  leituraBiomecanica,
  modoRaioX = false,
}: Props) {
  // Paleta de Crash-Test Dummy; no Raio-X, a mesma casca azul do VitimaProcedural,
  // sem as cores de atenção, para os órgãos se destacarem por dentro
  const corDummyCorpo = modoRaioX ? '#0284c7' : '#d97706' // Âmbar técnico
  const corDummyCabeca = modoRaioX ? '#38bdf8' : '#eab308' // Amarelo de teste
  const corEstrutural = modoRaioX ? '#0284c7' : '#1e293b' // Grafite chumbo de juntas
  const atencoesCasca = modoRaioX ? [] : atencoes
  const corPelve = corDeAtencao(atencoesCasca, 'pelve', corEstrutural)
  const corTronco = corDeAtencao(atencoesCasca, 'torax', corDummyCorpo)
  const corCabeca = corDeAtencao(atencoesCasca, 'cabeca', corDummyCabeca)

  const estresseCervicalCritico = leituraBiomecanica?.nivelCervical.classificacao === 'critico'
  const estresseCervicalAtencao = leituraBiomecanica?.nivelCervical.classificacao === 'atencao'
  const estresseJoelhos = leituraBiomecanica?.estresseJoelhos !== 'normal'
  const estresseCotovelos = leituraBiomecanica?.estresseCotovelos !== 'normal'

  const raioX = modoRaioX ? MATERIAL_RAIO_X : {}

  const preso = (segmento: MembroAprisionavel) =>
    aprisionamentos.some((item) => item.segmento === segmento)

  return (
    // o three compila `transparent` no shader (OPAQUE): alternar o Raio-X sem
    // recriar os materiais manteria a casca opaca escondendo os órgãos
    <group key={modoRaioX ? 'raio-x' : 'normal'}>
      {/* Pelve do Dummy com cinta estrutural */}
      <group
        position={paraArray(pelve.posicao)}
        quaternion={[pelve.rotacao.x, pelve.rotacao.y, pelve.rotacao.z, pelve.rotacao.w]}
      >
        <mesh>
          <boxGeometry args={[p.larguraPelve, p.alturaPelve, p.profundidadeTronco]} />
          <meshStandardMaterial
            color={corPelve}
            emissive={corPelve}
            emissiveIntensity={temAtencao(atencoesCasca, 'pelve') ? 0.32 : 0}
            roughness={0.65}
            metalness={0.2}
            {...raioX}
          />
        </mesh>
        {/* Faixa pélvica de calibração */}
        <mesh position={[0, 0, p.profundidadeTronco / 2 + 0.002]}>
          <planeGeometry args={[p.larguraPelve * 0.9, p.alturaPelve * 0.25]} />
          <meshStandardMaterial color="#f59e0b" roughness={0.5} />
        </mesh>
      </group>

      {/* Tronco / Caixa Torácica do Dummy com Placa Esternal e Target */}
      <group
        position={paraArray(tronco.posicao)}
        quaternion={[tronco.rotacao.x, tronco.rotacao.y, tronco.rotacao.z, tronco.rotacao.w]}
      >
        <mesh>
          <boxGeometry args={[p.larguraTronco, p.alturaTronco, p.profundidadeTronco]} />
          <meshStandardMaterial
            color={corTronco}
            emissive={corTronco}
            emissiveIntensity={temAtencao(atencoesCasca, 'torax') ? 0.28 : 0}
            roughness={0.65}
            metalness={0.15}
            {...raioX}
          />
        </mesh>

        {modoRaioX ? (
          // os órgãos medem a altura a partir da base do tronco; aqui a origem é o centro
          <group position={[0, -p.alturaTronco / 2, 0]}>
            <OrgaosTronco3D
              alturaTronco={p.alturaTronco}
              larguraTronco={p.larguraTronco}
              profundidadeTronco={p.profundidadeTronco}
            />
          </group>
        ) : (
          <>
            {/* Placa esternal dianteira (tórax de manequim Hybrid III) */}
            <mesh position={[0, 0.01, p.profundidadeTronco / 2 + 0.003]}>
              <boxGeometry args={[p.larguraTronco * 0.62, p.alturaTronco * 0.68, 0.008]} />
              <meshStandardMaterial color="#0f172a" roughness={0.5} metalness={0.25} />
            </mesh>

            {/* Alvo fotogramétrico central de crash test no esterno */}
            <group position={[0, 0.02, p.profundidadeTronco / 2 + 0.01]}>
              <MarcadorCrashTest raio={0.034} />
            </group>
          </>
        )}

        {/* Estrutura superior dos ombros com calotas laterais */}
        <mesh position={[0, p.alturaTronco / 2 - 0.04, 0]}>
          <boxGeometry args={[p.larguraOmbros, 0.09, p.profundidadeTronco * 0.88]} />
          <meshStandardMaterial color={corEstrutural} roughness={0.6} metalness={0.2} {...raioX} />
        </mesh>
      </group>

      {/* Cabeça do Dummy com alvos nas têmporas e testa */}
      <group
        position={paraArray(cabeca.posicao)}
        quaternion={[cabeca.rotacao.x, cabeca.rotacao.y, cabeca.rotacao.z, cabeca.rotacao.w]}
      >
        <mesh scale={[0.92, 1.08, 1]}>
          <sphereGeometry args={[p.raioCabeca, 22, 18]} />
          <meshStandardMaterial
            color={corCabeca}
            emissive={estresseCervicalCritico ? '#ef4444' : estresseCervicalAtencao ? '#f97316' : corCabeca}
            emissiveIntensity={
              modoRaioX ? 0 : estresseCervicalCritico ? 0.5 : estresseCervicalAtencao ? 0.25 : temAtencao(atencoes, 'cabeca') ? 0.3 : 0
            }
            roughness={0.55}
            metalness={0.1}
            {...raioX}
          />
        </mesh>

        {/* Encéfalo 3D com Coup-Contrecoup no Modo Raio-X */}
        {modoRaioX && <Cerebro3D raioCabeca={p.raioCabeca} />}

        {/* Faixa coronal característica de dummy de ensaio */}
        <mesh position={[0, 0, 0]} rotation={[0, 0, Math.PI / 2]} scale={[1.085, 0.925, 1.005]}>
          <torusGeometry args={[p.raioCabeca * 0.98, 0.006, 12, 32]} />
          <meshStandardMaterial color="#0f172a" roughness={0.4} />
        </mesh>

        {/* Alvo têmpora esquerda */}
        <group position={[-p.raioCabeca * 0.92, 0, 0]} rotation={[0, -Math.PI / 2, 0]}>
          <MarcadorCrashTest
            raio={0.03}
            destaque={estresseCervicalCritico}
            corDestaque="#ef4444"
          />
        </group>

        {/* Alvo têmpora direita */}
        <group position={[p.raioCabeca * 0.92, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
          <MarcadorCrashTest
            raio={0.03}
            destaque={estresseCervicalCritico}
            corDestaque="#ef4444"
          />
        </group>

        {/* Alvo na testa */}
        <group position={[0, p.raioCabeca * 0.25, p.raioCabeca * 0.94]} rotation={[0, 0, 0]}>
          <MarcadorCrashTest
            raio={0.024}
            destaque={estresseCervicalCritico}
            corDestaque="#ef4444"
          />
        </group>
      </group>

      {/* Membros com calotas articulares e marcadores */}
      <MembroFisico
        raioX={modoRaioX}
        quadro={membros.bracoEsquerdo}
        forma="braco"
        comprimento={p.comprimentoBraco}
        raio={p.raioMembro * 0.85}
        cor={corDummyCorpo}
        aprisionado={preso('bracoEsquerdo')}
        estresseArticular={estresseCotovelos}
      />
      <MembroFisico
        raioX={modoRaioX}
        quadro={membros.bracoDireito}
        forma="braco"
        comprimento={p.comprimentoBraco}
        raio={p.raioMembro * 0.85}
        cor={corDummyCorpo}
        aprisionado={preso('bracoDireito')}
        estresseArticular={estresseCotovelos}
      />
      <MembroFisico
        raioX={modoRaioX}
        quadro={membros.antebracoEsquerdo}
        forma="antebraco"
        comprimento={p.comprimentoAntebraco}
        raio={p.raioMembro * 0.72}
        cor={corDummyCabeca}
        aprisionado={preso('antebracoEsquerdo')}
        estresseArticular={estresseCotovelos}
      />
      <MembroFisico
        raioX={modoRaioX}
        quadro={membros.antebracoDireito}
        forma="antebraco"
        comprimento={p.comprimentoAntebraco}
        raio={p.raioMembro * 0.72}
        cor={corDummyCabeca}
        aprisionado={preso('antebracoDireito')}
        estresseArticular={estresseCotovelos}
      />

      <MembroFisico
        raioX={modoRaioX}
        quadro={membros.coxaEsquerda}
        forma="coxa"
        comprimento={p.comprimentoCoxa}
        raio={p.raioMembro * 1.15}
        cor={corDummyCorpo}
        aprisionado={preso('coxaEsquerda')}
        estresseArticular={estresseJoelhos}
      />
      <MembroFisico
        raioX={modoRaioX}
        quadro={membros.coxaDireita}
        forma="coxa"
        comprimento={p.comprimentoCoxa}
        raio={p.raioMembro * 1.15}
        cor={corDummyCorpo}
        aprisionado={preso('coxaDireita')}
        estresseArticular={estresseJoelhos}
      />
      <MembroFisico
        raioX={modoRaioX}
        quadro={membros.pernaEsquerda}
        forma="perna"
        comprimento={p.comprimentoPerna}
        raio={p.raioMembro * 0.92}
        cor={corDummyCorpo}
        aprisionado={preso('pernaEsquerda')}
        estresseArticular={estresseJoelhos}
      />
      <MembroFisico
        raioX={modoRaioX}
        quadro={membros.pernaDireita}
        forma="perna"
        comprimento={p.comprimentoPerna}
        raio={p.raioMembro * 0.92}
        cor={corDummyCorpo}
        aprisionado={preso('pernaDireita')}
        estresseArticular={estresseJoelhos}
      />
      <MembroFisico
        raioX={modoRaioX}
        quadro={membros.peEsquerdo}
        comprimento={Math.max(p.raioMembro * 3, 0.17)}
        raio={p.raioMembro * 0.82}
        cor={corEstrutural}
        aprisionado={preso('peEsquerdo')}
      />
      <MembroFisico
        raioX={modoRaioX}
        quadro={membros.peDireito}
        comprimento={Math.max(p.raioMembro * 3, 0.17)}
        raio={p.raioMembro * 0.82}
        cor={corEstrutural}
        aprisionado={preso('peDireito')}
      />

      {/* Pescoço cervical biomecânico segmentado (anéis elastoméricos do Hybrid III) */}
      <PescocoCervical
        de={tronco.posicao}
        para={cabeca.posicao}
        meiaAlturaTronco={p.alturaTronco / 2}
        raioCabeca={p.raioCabeca}
        estresseCritico={estresseCervicalCritico}
        estresseAtencao={estresseCervicalAtencao}
      />

      {mostrarEixoColuna && (
        <Line
          points={[paraArray(pelve.posicao), paraArray(cabeca.posicao)]}
          color={CORES_SEMANTICAS.eixoAnatomico}
          lineWidth={5}
        />
      )}
    </group>
  )
}

function MembroFisico({
  quadro,
  forma,
  comprimento,
  raio,
  cor,
  aprisionado,
  estresseArticular = false,
  raioX = false,
}: {
  quadro?: QuadroCorpo
  /** silhueta afilada do membro; sem ela, cápsula (os pés) */
  forma?: FormaDeMembro
  comprimento: number
  raio: number
  cor: string
  aprisionado: boolean
  estresseArticular?: boolean
  raioX?: boolean
}) {
  if (!quadro) return null
  const materialRaioX = raioX ? MATERIAL_RAIO_X : {}
  const corFinal = aprisionado ? '#eb5757' : estresseArticular ? '#ea580c' : cor
  const emissiveIntensity = aprisionado ? 0.45 : estresseArticular ? 0.35 : 0

  return (
    <group
      position={paraArray(quadro.posicao)}
      quaternion={[quadro.rotacao.x, quadro.rotacao.y, quadro.rotacao.z, quadro.rotacao.w]}
    >
      {/* o quadro é o centro do membro; a geometria afilada tem o pivô na origem */}
      <mesh position={forma ? [0, comprimento / 2, 0] : undefined}>
        {forma ? (
          <primitive object={geometriaDeMembro(forma, comprimento, raio)} attach="geometry" />
        ) : (
          <capsuleGeometry args={[raio, Math.max(comprimento - raio * 2, 0.02), 8, 14]} />
        )}
        <meshStandardMaterial
          color={corFinal}
          emissive={corFinal}
          emissiveIntensity={emissiveIntensity}
          roughness={0.65}
          metalness={0.15}
          {...materialRaioX}
        />
      </mesh>
      {/* Pivô articular em grafite no extremo proximal do membro */}
      <mesh position={[0, forma ? comprimento / 2 : (comprimento - raio) / 2, 0]}>
        <sphereGeometry args={[raio * (forma ? 0.97 : 0.96), 12, 10]} />
        <meshStandardMaterial color="#1e293b" roughness={0.6} metalness={0.3} {...materialRaioX} />
      </mesh>
    </group>
  )
}

/** Pescoço cervical biomecânico segmentado (anéis elastoméricos do manequim Hybrid III). */
function PescocoCervical({
  de,
  para,
  meiaAlturaTronco,
  raioCabeca,
  estresseCritico = false,
  estresseAtencao = false,
}: {
  de: Vetor3
  para: Vetor3
  meiaAlturaTronco: number
  raioCabeca: number
  estresseCritico?: boolean
  estresseAtencao?: boolean
}) {
  const dx = para.x - de.x
  const dy = para.y - de.y
  const dz = para.z - de.z
  const comprimento = Math.hypot(dx, dy, dz)
  if (comprimento < 1e-6) return null
  const direcao = { x: dx / comprimento, y: dy / comprimento, z: dz / comprimento }
  const inicio = {
    x: de.x + direcao.x * (meiaAlturaTronco - 0.02),
    y: de.y + direcao.y * (meiaAlturaTronco - 0.02),
    z: de.z + direcao.z * (meiaAlturaTronco - 0.02),
  }
  const fim = {
    x: para.x - direcao.x * (raioCabeca * 0.72),
    y: para.y - direcao.y * (raioCabeca * 0.72),
    z: para.z - direcao.z * (raioCabeca * 0.72),
  }

  const corPescoco = estresseCritico ? '#ef4444' : estresseAtencao ? '#f97316' : '#1e293b'
  const emissiveIntensity = estresseCritico ? 0.6 : estresseAtencao ? 0.3 : 0

  // 3 anéis elastoméricos concêntricos ao longo do pescoço
  const aneis = [0.25, 0.5, 0.75].map((t) => ({
    x: inicio.x + (fim.x - inicio.x) * t,
    y: inicio.y + (fim.y - inicio.y) * t,
    z: inicio.z + (fim.z - inicio.z) * t,
  }))

  return (
    <group>
      <Line points={[paraArray(inicio), paraArray(fim)]} color={corPescoco} lineWidth={10} />
      {aneis.map((pos, idx) => (
        <mesh key={idx} position={paraArray(pos)}>
          <sphereGeometry args={[0.036, 12, 10]} />
          <meshStandardMaterial
            color={corPescoco}
            emissive={corPescoco}
            emissiveIntensity={emissiveIntensity}
            roughness={0.5}
            metalness={0.2}
          />
        </mesh>
      ))}
    </group>
  )
}

const CORES_ATENCAO = {
  observacao: '#f2c94c',
  atencao: '#f2994a',
  alto: '#eb5757',
} as const

function temAtencao(atencoes: readonly AtencaoCorporal[], regiao: RegiaoCorporalAtencao): boolean {
  return atencoes.some((item) => item.regiao === regiao)
}

function corDeAtencao(
  atencoes: readonly AtencaoCorporal[],
  regiao: RegiaoCorporalAtencao,
  padrao: string,
): string {
  const atencao = atencoes.find((item) => item.regiao === regiao)
  return atencao ? CORES_ATENCAO[atencao.nivel] : padrao
}
